import { UseSurvey } from '@core/usecases';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormService, Logger } from '@core/services';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { PERMISSIONS, PermissionService } from '@auth';
import { DateTimeUtility, EnumToKeyValueArray, getAlertTypeByApprovedStatusOfEntity, GetTeamId, GetUserId, isMobileView } from '@core/utils';
import {
  GroupEntity,
  PaginatedEntity,
  Pagination,
  SurveyAnswerDetailsEntity,
  SurveyEntity,
  SurveyResultAnswerCountEntity,
  SurveyResultDetailsEntity,
  SurveyResultsEntity,
  TagEntity,
} from '@core/entities';
import { ConfirmationDialogType, DeleteConfirmationOptions, NotificationType, SocketEvents, SurveyNotificationOption, SurveyOptions, SurveyType, SurveyViewOption } from '@core/enums';
import { untilDestroyed } from '@ngneat/until-destroy';
import { instanceToPlain } from 'class-transformer';
import { HotToastService } from '@ngxpert/hot-toast';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { debounceTime } from 'rxjs';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';
import { SurveyVoterDetailsComponent } from '@shared/components';

const log = new Logger('Survey Composition');

export class SurveysComposition extends FormService {
  currentSurvey: SurveyEntity;
  surveys: SurveyEntity[] = [];
  pagination: Pagination = new PaginatedEntity({
    pagination: {
      page: 1,
      pageSize: 15,
    },
  }).pagination;
  tags: TagEntity[] = [];
  groups: Partial<GroupEntity>[] = [];
  surveyOptions;
  surveyTypes;
  surveyViewOptions;
  surveyNotificationOptions;
  resultsAfterVoting: SurveyAnswerDetailsEntity[];

  selectedImage: File;

  surveyLoaders = { isFormLoading: true, isResultLoading: true, isVotedByResultsLoading: false };

  // --- Properties for Survey Detail/Results Component ---

  surveyResults: SurveyResultDetailsEntity;
  userVotedAnswers: SurveyResultsEntity[];

  // ------------------------------------------------------

  protected readonly _translate = inject(TranslateService);
  protected readonly _dialogService = inject(DialogService);
  protected readonly _bottomSheet = inject(NgxBottomSheetService);
  protected dialogRef: DialogRef<any, boolean>;
  protected readonly _toast = inject(HotToastService);
  protected readonly _router = inject(Router);
  protected readonly _useSurvey = new UseSurvey();
  protected readonly _permissionService = inject(PermissionService);
  protected readonly _socketService = inject(SocketIoService);
  protected readonly _activatedRoute = inject(ActivatedRoute);

  private readonly _formBuilder = inject(FormBuilder);

  constructor() {
    super();
    this.surveyNotificationOptions = EnumToKeyValueArray(SurveyNotificationOption, this._translate);
    this.surveyViewOptions = EnumToKeyValueArray(SurveyViewOption, this._translate);
    this.surveyTypes = EnumToKeyValueArray(SurveyType, this._translate);
    this.surveyOptions = EnumToKeyValueArray(SurveyOptions, this._translate);
  }

  get canCreateSurvey() {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_SURVEY);
  }

  get answers(): FormArray {
    return this.form.get('answers') as FormArray;
  }

  get alertType() {
    return getAlertTypeByApprovedStatusOfEntity(this.currentSurvey);
  }

  // Method to add an answer
  addAnswer() {
    (this.form.get('answers') as FormArray).push(this._formBuilder.control('', Validators.required));
  }

  close() {
    if (!isMobileView()) {
      this._dialogService.closeAll();
    } else {
      this._bottomSheet.close();
    }
  }

  // Method to remove an answer
  removeAnswer(index: number) {
    (this.form.get('answers') as FormArray).removeAt(index);
  }

  save(btnRef?: ButtonComponent) {
    if (this.form.invalid) return;
    const form = this.form.value;
    form.notificationOption = [form.notificationOption];
    form.groupType = form.groupType ? [form.groupType] : [];
    const surveyData = new SurveyEntity(form);
    const transformedData = this.removeEventFields(this.removeNullObjects(instanceToPlain(surveyData)));
    const formData = this.jsonToFromData(transformedData, ['surveyNotificationOption', 'tags', 'survey_Answers', 'groupType']);

    formData.append('user_id', GetUserId());
    formData.delete('approved_status');
    formData.append('approved_status', this._permissionService.isAdmin() || this._permissionService.isFunctionary() ? '1' : '0');
    formData.append('image', '');
    formData.delete('survey_status');
    formData.delete('survey_type');
    formData.delete('surveyAnswer');
    formData.delete('created_by');
    formData.delete('surveyTags');

    if (this.selectedImage) {
      formData.append('image', this.selectedImage, this.selectedImage.name);
    }

    // console.log('FORM DATA', formData);
    //
    // return;

    const service$ = this.currentSurvey && this.currentSurvey?.id ? this._useSurvey.updateSurvey(this.currentSurvey.id, formData) : this._useSurvey.addNewSurvey(formData);

    this.call(service$, btnRef)
      .then((response) => {
        if (response) {
          if (!this._permissionService.isAdmin()) {
            this._toast.success(this._translate.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
          } else {
            const message = this.currentSurvey && this.currentSurvey?.id ? 'Update Successful' : 'Created Successfully';
            this._toast.success(this._translate.instant(message));
          }

          this._socketService.emit(SocketEvents.SendNotification, NotificationType.Survey);
          if (!isMobileView()) {
            this.dialogRef.close(true);
            this._dialogService.closeAll();
          } else {
            this._bottomSheet.closeAll();
          }
          // this._reloadCurrentRoute();
        } else {
          this._toast.error(this._translate.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while creating or updating survey');
      });
  }

  saveVote(btnRef?: ButtonComponent) {
    if (this.form.invalid) {
      // print all invalid controls
      Object.keys(this.form.controls).forEach((key) => {
        console.log(key, this.form.controls[key].errors);
      });
    }
    const formData = this.form.value;

    this.call(this._useSurvey.addSurveyVoting(formData), btnRef)
      .then((response) => {
        if (response) {
          this._toast.success(this._translate.instant('Vote Successful'));
          this._socketService.emit(SocketEvents.SendNotification, NotificationType.Survey);
          // this._reloadCurrentRoute();
          if (!isMobileView()) {
            this._dialogService.closeAll();
          } else {
            this._bottomSheet.close();
          }
        } else {
          this._toast.error(this._translate.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while voting for survey');
      });
  }

  deleteSurvey(survey: SurveyEntity) {
    if (!survey.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheet.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translate.instant('Survey'),
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useSurvey.deleteSurvey(survey.id)).then(() => {
                  this._bottomSheet.closeAll();
                  this._toast.success(this._translate.instant('Delete Successful'));
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.Survey);
                  // this._reloadCurrentRoute();
                });
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: survey.title,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this.call(this._useSurvey.deleteSurvey(survey.id)).then(() => {
              this._toast.success(this._translate.instant('Delete Successful'));
              this._dialogService.closeAll();
              this._socketService.emit(SocketEvents.SendNotification, NotificationType.Survey);
              // this._reloadCurrentRoute();
            });
          }
        },
      });
    }
  }

  onActions(actionData: { actionType: string; survey: SurveyEntity }) {
    if (!actionData.survey || !actionData.survey.id) return;
    switch (actionData.actionType) {
      case 'view':
        // console.log('view');
        break;
      case 'edit':
        // console.log('edit');
        break;
      case 'delete':
        this.deleteSurvey(actionData.survey);
        break;
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------
  protected _fetchAllSurveyResultById(): void {
    this.call(this._useSurvey.getSurveyResults(this.currentSurvey.id)).then((res) => {
      this.surveyResults = res;
      this.currentSurvey = res.surveyDetails[0];
      this.surveyLoaders.isFormLoading = false;

      // Percentage Calculate
      if (this.surveyResults && this.surveyResults.answerCount && this.surveyResults.answerCount.length > 0) {
        this.surveyResults.answerCount.forEach((item: SurveyResultAnswerCountEntity, index) => {
          if (this.surveyResults.totalCount !== 0) {
            item.percentageValue = (item.count / this.surveyResults.totalCount) * 100;
          } else {
            item.percentageValue = 0;
          }
        });
      }
      this._fetchUserVotedAnswers();

      // Sort Answer list
      // this.currentSurvey.surveyAnswer = this.currentSurvey.surveyAnswer.sort((a, b) => b.id - a.id);
    });
  }

  protected _fetchUserVotedAnswers() {
    this._useSurvey
      .getUserVotedSurveyResults(this.currentSurvey.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.userVotedAnswers = response;
          this.surveyLoaders.isResultLoading = false;
        },
      });
  }

  protected _fetchAllActiveSurveys(): void {
    this.call(this._useSurvey.getAllActiveSurveys(this.pagination)).then((surveyList) => {
      if (!surveyList) return;
      this.pagination = surveyList?.pagination;
      this.surveys = surveyList?.survey;
    });
  }

  protected _fetchAllCompletedSurveys(): void {
    this.call(this._useSurvey.getAllCompletedSurveys(this.pagination)).then((surveyList) => {
      if (!surveyList) return;
      this.pagination = surveyList?.pagination;
      this.surveys = surveyList?.survey;
    });
  }

  protected _fetchAllMySurveys(): void {
    this.call(this._useSurvey.getAllMySurveys(this.pagination)).then((surveyList) => {
      if (!surveyList) return;
      this.pagination = surveyList?.pagination;
      this.surveys = surveyList?.survey;
    });
  }

  protected _fetchSurvey(surveyId: number) {
    this.call(this._useSurvey.getSurveyById(surveyId)).then((survey) => {
      this.currentSurvey = this.removeNullObjects(survey);
      this._patchForm(survey);
      this.surveyLoaders.isFormLoading = false;
    });
  }

  protected _fetchDataForForm() {
    this._fetchSurveyTags();
    this._fetchTeamGroups();
  }

  protected _fetchSurveyById(surveyId: number, isEdit: boolean) {
    this.call(this._useSurvey.getSurveyById(surveyId)).then((response) => {
      if (this.currentSurvey !== response) {
        this.currentSurvey = response;
        // this.surveyLoaders.isFormLoading = false;

        if (isEdit) {
          this._patchForm(response);
        }
      } else if (isEdit) {
        this._patchForm(response);
      }

      this._fetchAllSurveyResultById();
    });
  }

  protected _showVotingByResults(surveyId: number, answerId: number) {
    this.surveyLoaders.isVotedByResultsLoading = true;
    this._useSurvey
      .getVotingResult(surveyId, answerId)
      .pipe(untilDestroyed(this), debounceTime(5000))
      .subscribe({
        next: (response) => {
          this.resultsAfterVoting = response;

          if (!isMobileView()) {
            this._dialogService.open(SurveyVoterDetailsComponent, {
              size: 'md',
              closeButton: true,
              data: {
                surveyResult: this.resultsAfterVoting,
                currentSurvey: this.currentSurvey,
              },
            });
          } else {
            this._bottomSheet.open(SurveyVoterDetailsComponent, {
              height: 'mid',
              data: {
                surveyResult: this.resultsAfterVoting,
                currentSurvey: this.currentSurvey,
              },
            });
          }
          this.surveyLoaders.isResultLoading = false;
          this.surveyLoaders.isVotedByResultsLoading = false;
        },
        error: (error) => {
          log.error('Error while fetching survey voting results');
          this.surveyLoaders.isResultLoading = false;
          this.surveyLoaders.isVotedByResultsLoading = false;
        },
      });
  }

  protected _initForm() {
    this.form = this._formBuilder.group({
      title: [null, [Validators.required]],
      description: [null, [Validators.required]],
      option: [this.surveyOptions[0].value, [Validators.required]],
      type: [null, [Validators.required]],
      groupType: [null],
      endDate: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      viewOption: [null, [Validators.required]],
      additionalAnonymousVoting: [false, [Validators.required]],
      additionalCastVote: [false, [Validators.required]],
      notificationOption: [1], // removed required from this as it not required as per request
      answers: this._formBuilder.array([], [Validators.required, this._minAnswersValidator(2)]),
      tags: [[]],
      approvedStatus: [0, [Validators.required]],
      userId: [GetUserId(), [Validators.required]],
      teamId: [GetTeamId(), [Validators.required]],
      image: [null],
    });

    this.form.get('approvedStatus').setValue(this._permissionService.isAdmin() || this._permissionService.isFunctionary() ? 1 : 0, {
      emitEvent: false,
    });

    this.form
      .get('type')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value === SurveyType.Group) {
            this.form.get('groupType').setValidators([Validators.required]);
          } else {
            this.form.get('groupType').clearValidators();
          }
          this.form.get('groupType').updateValueAndValidity();
        },
      });
  }

  protected _initSurveyVoteForm() {
    this.form = this._formBuilder.group({
      user_id: [+GetUserId(), [Validators.required]],
      team_id: [+GetTeamId(), [Validators.required]],
      surveyId: [this.currentSurvey.id, [Validators.required]],
      surveyType: [this.currentSurvey.type, [Validators.required]],
      surveyOption: [this.currentSurvey.option, [Validators.required]],
      surveyAnswerId: [null, [Validators.required]],
    });
  }

  private _patchForm(survey: SurveyEntity) {
    // Patch the form values except for the 'answers' FormArray
    this.form.patchValue({
      title: survey.title,
      description: survey.description,
      option: +survey.option,
      type: survey.type,
      groupType: survey.groupType?.length > 0 ? survey.groupType[0] : null,
      endDate: DateTimeUtility.getDateOnly(survey.endDate),
      startDate: DateTimeUtility.getDateOnly(survey.startDate),
      viewOption: +survey.viewOption,
      additionalAnonymousVoting: this.parseBoolean(survey.additionalAnonymousVoting),
      additionalCastVote: this.parseBoolean(survey.additionalCastVote),
      notificationOption: survey?.notificationOption[0] || null,
      tags: survey.tags,
      approvedStatus: survey.status,
      userId: survey.userId,
      teamId: survey.teamId,
      image: survey.imageUrl,
    });

    // Clear existing answers FormArray
    const answersArray = this.form.get('answers') as FormArray;
    answersArray.clear();

    // Add new controls for each answer
    survey.answers.forEach((answer) => {
      answersArray.push(this._formBuilder.control(answer));
    });
  }

  private _fetchSurveyTags() {
    this.call(this._useSurvey.getSurveyTags()).then((response) => {
      this.tags = this.removeNullObjects(response) as TagEntity[];
    });
  }

  private _fetchTeamGroups() {
    this.call(this._useSurvey.getTeamGroups()).then((response) => {
      this.groups = this.removeNullObjects(response) as Partial<GroupEntity>[];
    });
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }

  // Validator for minimum answers
  private _minAnswersValidator(min: number) {
    return (formArray: FormArray) => {
      return formArray.length >= min ? null : { minAnswers: true };
    };
  }
}
